import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import CourierLayout from '../CourierLayout';
import { API } from '../../../config';

const VehicleMaintenance = () => {
  const [vehicles, setVehicles] = useState([]);
  const [maintenanceType, setMaintenanceType] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const token =
    localStorage.getItem('courier_token') ||
    localStorage.getItem('token');

  const normalizeVehicles = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.vehicles)) return data.vehicles;
    if (Array.isArray(data?.rows)) return data.rows;
    return [];
  };

  const fetchVehicles = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${API}/api/courier/vehicles`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        setVehicles([]);
        return;
      }

      const data = await response.json();
      setVehicles(normalizeVehicles(data));
    } catch (error) {
      console.error('Failed to load vehicles:', error);
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scheduleMaintenance = async () => {
    try {
      setMessage('');

      if (!selectedVehicle) {
        setMessage('Please select a vehicle.');
        return;
      }

      if (!maintenanceType) {
        setMessage('Please select a maintenance type.');
        return;
      }

      if (!scheduledDate) {
        setMessage('Please select a scheduled date.');
        return;
      }

      const response = await fetch(`${API}/api/courier/vehicle-maintenance`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vehicle_id: selectedVehicle,
          maintenance_type: maintenanceType,
          scheduled_date: scheduledDate,
          notes,
        }),
      });

      if (!response.ok) {
        throw new Error('Unable to schedule maintenance.');
      }

      setMessage('Maintenance scheduled.');
      setMaintenanceType('');
      setSelectedVehicle('');
      setScheduledDate('');
      setNotes('');
      fetchVehicles();
    } catch (error) {
      setMessage(error.message || 'Unable to schedule maintenance.');
    }
  };

  const renderEmptyState = () => (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, md: 4 },
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: 3,
        textAlign: 'center',
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: '#64748b',
          fontSize: '0.95rem',
        }}
      >
        No vehicles found. Add vehicle details in your profile before scheduling maintenance.
      </Typography>
    </Paper>
  );

  return (
    <CourierLayout>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontSize: '2rem',
            fontWeight: 700,
            lineHeight: 1.25,
            color: '#0f172a',
            mb: 1,
            textAlign: 'center',
          }}
        >
          Vehicle Maintenance
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            textAlign: 'center',
            fontSize: '0.95rem',
            lineHeight: 1.5,
          }}
        >
          Track vehicle records and schedule maintenance when needed.
        </Typography>
      </Box>

      {message && (
        <Alert
          severity={message.includes('scheduled') ? 'success' : 'warning'}
          sx={{ mb: 2, borderRadius: 2 }}
        >
          {message}
        </Alert>
      )}

      <Paper
        elevation={1}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          border: '1px solid rgba(15, 23, 42, 0.08)',
          backgroundColor: '#ffffff',
          mb: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: '#0f172a',
            fontWeight: 700,
            mb: 0.75,
          }}
        >
          Vehicles
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            fontSize: '0.95rem',
            mb: 2,
          }}
        >
          Maintenance records will appear for vehicles connected to your profile.
        </Typography>

        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Typography sx={{ color: '#64748b', textAlign: 'center', py: 3 }}>
            Loading vehicles...
          </Typography>
        ) : vehicles.length === 0 ? (
          renderEmptyState()
        ) : (
          <Grid container spacing={2}>
            {vehicles.map((vehicle) => {
              const vehicleId = vehicle.vehicle_id || vehicle.id;
              const title =
                vehicle.name ||
                `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`.trim() ||
                `Vehicle #${vehicleId}`;

              return (
                <Grid item xs={12} key={vehicleId}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      backgroundColor: '#f8fafc',
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 1,
                      }}
                    >
                      <Box>
                        <Typography sx={{ color: '#0f172a', fontWeight: 700 }}>
                          {title}
                        </Typography>

                        <Typography sx={{ color: '#64748b', fontSize: '0.95rem' }}>
                          Plate: {vehicle.license_plate || vehicle.licensePlate || 'Not provided'}
                        </Typography>

                        <Typography sx={{ color: '#64748b', fontSize: '0.95rem' }}>
                          Last Maintenance: {vehicle.last_maintenance || vehicle.lastMaintenance || 'Not recorded'}
                        </Typography>
                      </Box>

                      <Chip
                        label={vehicle.status || 'Active'}
                        size="small"
                        sx={{
                          backgroundColor: '#dcfce7',
                          color: '#166534',
                          fontWeight: 700,
                        }}
                      />
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Paper>

      <Paper
        elevation={1}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          border: '1px solid rgba(15, 23, 42, 0.08)',
          backgroundColor: '#ffffff',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: '#0f172a',
            fontWeight: 700,
            mb: 0.75,
          }}
        >
          Schedule Maintenance
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            fontSize: '0.95rem',
            mb: 2,
          }}
        >
          Schedule service only for vehicles connected to your profile.
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Vehicle"
              fullWidth
              value={selectedVehicle}
              onChange={(event) => setSelectedVehicle(event.target.value)}
            >
              {vehicles.length === 0 && (
                <MenuItem value="">No vehicles available</MenuItem>
              )}

              {vehicles.map((vehicle) => {
                const vehicleId = vehicle.vehicle_id || vehicle.id;
                const title =
                  vehicle.name ||
                  `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`.trim() ||
                  `Vehicle #${vehicleId}`;

                return (
                  <MenuItem key={vehicleId} value={vehicleId}>
                    {title}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Maintenance Type"
              fullWidth
              value={maintenanceType}
              onChange={(event) => setMaintenanceType(event.target.value)}
            >
              <MenuItem value="Oil Change">Oil Change</MenuItem>
              <MenuItem value="Tire Service">Tire Service</MenuItem>
              <MenuItem value="Brake Service">Brake Service</MenuItem>
              <MenuItem value="Inspection">Inspection</MenuItem>
              <MenuItem value="General Maintenance">General Maintenance</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Scheduled Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={scheduledDate}
              onChange={(event) => setScheduledDate(event.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Notes"
              placeholder="Optional maintenance notes"
              fullWidth
              multiline
              minRows={3}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
              <Button
                variant="contained"
                onClick={scheduleMaintenance}
                disabled={vehicles.length === 0}
                sx={{
                  minHeight: 42,
                  px: 4,
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Schedule Maintenance
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </CourierLayout>
  );
};

export default VehicleMaintenance;